export type OklchColor = {l: number; c: number; h: number};

export enum GraphBaseColor {
    corporateBlue = "aded70bf-be27-4548-96f8-a620fbf97d17",
    softTerracotta = "1c66e3bd-bd15-428d-b102-455431b3b785",
    jadeEmerald = "a87b6b5b-9bef-484f-aba3-1a271a82eb90",
    heatherPurple = "08ef3aa4-c2ed-48ff-b5ae-b5b63fe9d445",
    skyTeal = "5321105d-51a4-4094-984f-22ca75d98bb0",
    dustyCoral = "8700d2d3-5016-44a0-9025-0cef39400f50",
    periwinkle = "b66a6eb5-b112-4e40-97ee-775b5233c5e1",
    mutedRose = "896e6bb7-8ef1-45a1-a33b-a953056ad13c",
    deepMoss = "c7f6160f-31f1-4105-a06d-64660629aea1",
    neutral = "4fd49d13-9d2a-4790-96bb-24afda653af2",
}

export const GRAPH_BASE_COLOR_VALUES: Record<GraphBaseColor, OklchColor> = {
    [GraphBaseColor.corporateBlue]: {l: 0.7569, c: 0.0662, h: 250.41},
    [GraphBaseColor.softTerracotta]: {l: 0.8009, c: 0.0648, h: 70.43},
    [GraphBaseColor.jadeEmerald]: {l: 0.8322, c: 0.0789, h: 173.48},
    [GraphBaseColor.heatherPurple]: {l: 0.717, c: 0.1102, h: 307.48},
    [GraphBaseColor.skyTeal]: {l: 0.8146, c: 0.0645, h: 209.67},
    [GraphBaseColor.dustyCoral]: {l: 0.7286, c: 0.0883, h: 19.42},
    [GraphBaseColor.periwinkle]: {l: 0.6848, c: 0.1059, h: 283.25},
    [GraphBaseColor.mutedRose]: {l: 0.7387, c: 0.0979, h: 349.3},
    [GraphBaseColor.deepMoss]: {l: 0.8408, c: 0.1048, h: 131.16},
    [GraphBaseColor.neutral]: {l: 0.7532, c: 0.0046, h: 106.51},
};

export const GRAPH_BASE_COLOR_LABELS: Record<GraphBaseColor, string> = {
    [GraphBaseColor.corporateBlue]: "Corporate Blue",
    [GraphBaseColor.softTerracotta]: "Soft Terracotta",
    [GraphBaseColor.jadeEmerald]: "Jade Emerald",
    [GraphBaseColor.heatherPurple]: "Heather Purple",
    [GraphBaseColor.skyTeal]: "Sky Teal",
    [GraphBaseColor.dustyCoral]: "Dusty Coral",
    [GraphBaseColor.periwinkle]: "Periwinkle",
    [GraphBaseColor.mutedRose]: "Muted Rose",
    [GraphBaseColor.deepMoss]: "Deep Moss",
    [GraphBaseColor.neutral]: "Neutral",
};

export function getBaseColor(color: GraphBaseColor): string {
    const {l, c, h} = GRAPH_BASE_COLOR_VALUES[color];
    return `oklch(${l} ${c} ${h})`;
}

/** sRGB hex for Recharts / SVG fills where `oklch()` is not reliably supported. */
function oklchComponentsToHex(l: number, c: number, h: number): string {
    const hueRadians = (h * Math.PI) / 180;
    const a = c * Math.cos(hueRadians);
    const b = c * Math.sin(hueRadians);
    const l_ = l + 0.3963377774 * a + 0.2158037573 * b;
    const m_ = l - 0.1055613458 * a - 0.0638541728 * b;
    const s_ = l - 0.0894841775 * a - 1.291485548 * b;
    const l3 = l_ * l_ * l_;
    const m3 = m_ * m_ * m_;
    const s3 = s_ * s_ * s_;
    const linearRgb = [
        4.0767416621 * l3 - 3.3077115913 * m3 + 0.2309699292 * s3,
        -1.2684380046 * l3 + 2.6097574011 * m3 - 0.3413193965 * s3,
        -0.0041960863 * l3 - 0.7034186147 * m3 + 1.707614701 * s3,
    ];
    const srgb = linearRgb.map((channel) => {
        const clamped = Math.min(1, Math.max(0, channel));
        return clamped <= 0.0031308 ? 12.92 * clamped : 1.055 * clamped ** (1 / 2.4) - 0.055;
    });
    return (
        "#"
        + srgb
            .map((channel) => Math.round(channel * 255).toString(16).padStart(2, "0"))
            .join("")
    );
}

export function getBaseColorHex(color: GraphBaseColor): string {
    const {l, c, h} = GRAPH_BASE_COLOR_VALUES[color];
    return oklchComponentsToHex(l, c, h);
}

export const graphColors = [
    "#EF9B20",
    // "#EDBF33",
    // "#EDE15B",
    // "#BDCF32",
    "#87BC45",
    "#27AEEF",
    "#B33DC6",
    "#77D69F",
    "#FFAB73",
    "#FF9C97",
    "#EA5545",
    "#F46A9B",
    "#A084CA",
    "#7EC4CF",
    "#77D69F",
];

export const pieColors = [
    "#7950f2", // violet-6
    "#12b886", // teal-6
    "#FAB005", // yellow-6
    "#228BE6", // blue-6
    "#BE4BDB", // grape-6
    "#74B816", // lime-7
    "#E64980", // pink-6
    "#2F9E44", // green-8
    "#4DABF7", // blue-4
    "#38D9A9", // teal-4
    "#FD7E14", // orange-6
];
